/**
 * M3 — review strip. "N things to review", the newest item's person and
 * provenance, and on S1 the first item inline with keep / skip. This is the
 * module that proves "f8 remembers". Class 2, never dismissable.
 *
 * The inline decision collapses to a receipt with undo. When a host
 * provides HomeInlineContext (the desk, PPL-417) the receipt is the
 * server's answer — kept, skipped, not saved, or the buttons back after a
 * failure; without one (the fixtures page) the module keeps its own
 * optimistic state.
 *
 * DECLARED GAP (PPL-454): items f8 saved BY ITSELF do not reach this module.
 * Its state comes from `useHomeInline()` and the server-rendered
 * `ReviewPayload`, not from `WritebackContext`, so the new auto-saved
 * subscription and its `serverResolved` overlay stop at /insights and the
 * contact sidebar — `payload.count` is the PENDING count and the receipt line
 * only ever fires for an item kept inline this session. Nothing here shows a
 * WRONG number; the desk simply says nothing about a save. The app has the
 * identical gap for the same reason (its desk owns its own writeback wiring
 * in `useHomeActions.ts`). Wiring both desks to the receipt feed is a
 * follow-up, not this ticket — the copy below is the only half that landed.
 */

import { useState } from 'react';
import { HomeAction, HomeActions, HomeCard, HomeChevron } from '../HomeKit';
import { plural } from '../format';
import { useHomeInline, type ReviewInline } from '../inlineState';
import type { ModuleProps } from '../moduleTypes';

/** What a write-back kind is, in f8's words. Unknown kinds show nothing extra rather than the raw key. */
const KIND_PHRASE: Record<string, string> = {
  date: 'a date f8 noticed',
  fact: 'something f8 noted',
  commitment: 'a commitment f8 noticed',
  relationship_note: 'a note on how things are',
  relationship: 'a note on how things are',
  calendar_attendee: 'someone from your calendar',
};

/**
 * The receipt line per state. Status words, with one deliberate exception
 * (PPL-454): a kept item names the person it was saved to, because "kept" on
 * its own does not tell the user WHERE it went, and the whole point of a
 * receipt under the new policy is that f8 says what it did. Falls back to the
 * bare status word when the payload carries no name.
 */
function receiptLine(status: ReviewInline['status'], personName?: string | null): string {
  switch (status) {
    // A refused undo leaves the item kept, so it reads the same.
    case 'kept':
    case 'conflicted':
      return personName ? `saved to ${personName}` : 'saved';
    case 'skipped': return 'skipped';
    case 'undone': return 'not saved';
    case 'failed': return 'didn’t save — try again';
    default: return 'saved';
  }
}

export function ReviewModule({ module, onAction }: ModuleProps) {
  const hosted = useHomeInline();
  // Keyed on the item, so a refreshed payload whose first item changed under
  // the same dismissKey never inherits the last item's "kept · undo".
  const [local, setLocal] = useState<{ itemId: string; decision: 'keep' | 'skip' } | null>(null);
  if (module.type !== 'review') return null;
  const { payload } = module;
  const act = (a: Parameters<NonNullable<typeof onAction>>[0]) => onAction?.(a, module);

  const newest = payload.newest;
  const phrase = newest ? KIND_PHRASE[newest.kind] : undefined;
  const sub = newest ? `newest: ${newest.personName}${phrase ? ` — ${phrase}` : ''}` : undefined;
  const inline = payload.inline && payload.firstItem;
  const item = payload.firstItem;

  let state: ReviewInline | null = null;
  if (item) {
    if (hosted) state = hosted.review(item.id);
    else if (local?.itemId === item.id) state = { status: local.decision === 'keep' ? 'kept' : 'skipped', undoable: true };
  }
  const buttons = inline && item && (!state || state.status === 'failed');

  const decide = (d: 'keep' | 'skip') => {
    if (!item) return;
    if (!hosted) setLocal({ itemId: item.id, decision: d });
    act({ kind: 'review', itemId: item.id, decision: d });
  };
  const undo = () => {
    if (!item) return;
    if (!hosted) setLocal(null);
    act({ kind: 'review', itemId: item.id, decision: 'undo' });
  };

  return (
    <HomeCard
      reason={module.reason}
      body={`${plural(payload.count, 'thing')} to review`}
      sub={sub}
      right={inline ? null : <HomeChevron />}
      dismissable={false}
      onOpen={() => act({ kind: 'open' })}
      secondary={
        buttons ? (
          <HomeActions>
            <HomeAction primary onClick={() => decide('keep')}>keep</HomeAction>
            <HomeAction onClick={() => decide('skip')}>skip</HomeAction>
          </HomeActions>
        ) : null
      }
    >
      {inline && item ? (
        <div className={`home-quote${state?.status === 'failed' ? ' home-quote--warn' : ''}`}>
          {!state || state.status === 'failed' ? (
            <>
              “{item.text}”
              <span className="home-quote-who">{item.personName}</span>
            </>
          ) : null}
          {state ? (
            <span className="home-receipt">
              {receiptLine(state.status, item.personName)}
              {state.undoable ? (
                <>
                  {' · '}
                  <button
                    type="button"
                    className="home-undo-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      undo();
                    }}
                  >
                    undo
                  </button>
                </>
              ) : null}
            </span>
          ) : null}
        </div>
      ) : null}
    </HomeCard>
  );
}
